import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Shield, TrendingUp, CheckCircle, Menu, X, Globe, Award, ExternalLink, Zap, Lock, Target, Calculator, Send, Euro, Building2, AlertCircle, Linkedin } from 'lucide-react';
import { translations } from './translations';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [language, setLanguage] = useState('nl');
  
  // Calculator form state
  const [loanAmount, setLoanAmount] = useState(500000);
  const [collateralType, setCollateralType] = useState('');
  const [otherCollateral, setOtherCollateral] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error
  const [formErrors, setFormErrors] = useState({});
  
  // Expanded cases state (for mobile "Lees meer")
  const [expandedCases, setExpandedCases] = useState({});
  
  // Submissions go to our own backend (stores the lead first, then emails via Brevo).
  // Relative path -> nginx proxies /api to the api container in production.
  const WEBHOOK_URL = '/api/lead';

  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'calculator', 'intro', 'cases', 'sponsor', 'rules', 'process', 'about'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track phone-number clicks as a Google Ads lead conversion (same as form submit)
  useEffect(() => {
    const handlePhoneClick = (e) => {
      const link = e.target.closest('a[href^="tel:"]');
      if (!link) return;

      // One lead conversion per visitor session (shared with form-submit on /bedankt)
      if (sessionStorage.getItem('bbl_conversion_fired')) return;
      sessionStorage.setItem('bbl_conversion_fired', '1');

      // GA4 / GTM dataLayer marker (so phone calls are distinguishable in analytics)
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'phone_call', lead_type: 'phone' });

      // Google Ads lead conversion (same action as form submit)
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'formulierr', { lead_type: 'phone' });
      }
    };

    document.addEventListener('click', handlePhoneClick);
    return () => document.removeEventListener('click', handlePhoneClick);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'nl' ? 'en' : 'nl');
  };

  // Format currency for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!companyName.trim()) errors.companyName = true;
    if (!phone.trim()) errors.phone = true;
    if (!email.trim()) errors.email = true;
    if (!collateralType) errors.collateralType = true;
    if (collateralType === 'other' && !otherCollateral.trim()) errors.otherCollateral = true;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setFormStatus('sending');

    const collateralLabel = collateralType === 'other' 
      ? otherCollateral 
      : t.calculator.collateralOptions[collateralType];

    const payload = {
      loanAmount: loanAmount,
      loanAmountFormatted: formatCurrency(loanAmount),
      collateralType: collateralType,
      collateralLabel: collateralLabel,
      otherCollateral: collateralType === 'other' ? otherCollateral : '',
      companyName: companyName,
      phone: phone,
      email: email || 'Niet opgegeven / Not provided',
      language: language,
      timestamp: new Date().toISOString(),
      source: 'Bladt Bridge Loan Website Calculator'
    };

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Push GTM event
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'form_submit',
          'form_name': 'calculator_form',
          'form_type': 'loan_application',
          'loan_amount': loanAmount,
          'collateral_type': collateralType
        });
        
        // Google Analytics gtag event for conversion tracking
        if (window.gtag) {
          window.gtag('event', 'formulier', {
            event_category: 'lead',
            event_label: 'loan_application',
            value: 1
          });
        }
        
        // Redirect to separate thank you page
        window.location.href = '/bedankt';
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      console.error('Webhook error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  // Collateral options for dropdown
  const collateralOptions = [
    { value: 'inventory', label: t.calculator.collateralOptions.inventory },
    { value: 'art', label: t.calculator.collateralOptions.art },
    { value: 'cars', label: t.calculator.collateralOptions.cars },
    { value: 'watches', label: t.calculator.collateralOptions.watches },
    { value: 'ships', label: t.calculator.collateralOptions.ships },
    { value: 'realEstate', label: t.calculator.collateralOptions.realEstate },
    { value: 'invoices', label: t.calculator.collateralOptions.invoices },
    { value: 'shares', label: t.calculator.collateralOptions.shares },
    { value: 'other', label: t.calculator.collateralOptions.other }
  ];

  const navigationItems = [
    { id: 'home', label: t.nav.welcome },
    { id: 'calculator', label: language === 'nl' ? 'Calculator' : 'Calculator' },
    { id: 'cases', label: t.nav.cases },
    { id: 'sponsor', label: t.nav.sponsor },
    { id: 'rules', label: t.nav.rules },
    { id: 'process', label: t.nav.process },
    { id: 'about', label: t.nav.about }
  ];

  const cases = [
    t.cases.case1,
    t.cases.case2,
    t.cases.case3,
    t.cases.case4,
    t.cases.case5,
    t.cases.case6,
    t.cases.case7,
    t.cases.case8,
    t.cases.case9
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img
                src="/logo.png"
                alt="Bladt Bridge Loan"
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="text-2xl font-bold text-white" style={{display: 'none'}}>
                Bladt Bridge Loan
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden nav:flex items-center space-x-4">
              <div className="flex items-baseline space-x-4">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                      activeSection === item.id
                        ? 'bg-white/20 text-white'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="ml-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white font-medium transition-all duration-300 flex items-center space-x-2"
              >
                <Globe size={18} />
                <span>{language === 'nl' ? 'EN' : 'NL'}</span>
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="nav:hidden flex items-center space-x-2">
              <button
                onClick={toggleLanguage}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium"
              >
                {language === 'nl' ? 'EN' : 'NL'}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white hover:text-white/80 p-2"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="nav:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-black/50 backdrop-blur-lg">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-md w-full text-left"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block p-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mb-8">
              <div className="bg-slate-900 rounded-full px-6 py-2">
                <span className="text-white font-semibold">{t.hero.badge}</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
              {t.hero.title}
            </h1>

            <h2 className="text-2xl md:text-3xl text-blue-300 font-semibold mb-6">
              {t.hero.subtitle}
            </h2>

            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-4xl mx-auto">
              {t.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <div className="bg-green-500/20 backdrop-blur-sm border border-green-400/30 rounded-full px-6 py-3">
                <span className="text-green-300 font-semibold flex items-center">
                  <Clock className="mr-2" size={20} />
                  {t.hero.quickCredit}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('cases')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
              >
                {t.hero.viewCases}
              </button>
              <button
                onClick={() => window.location.href = 'tel:+31613265193'}
                className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105"
              >
                {t.hero.applyNow}
              </button>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-10 animate-pulse hidden lg:block">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
            <TrendingUp className="text-blue-400" size={32} />
          </div>
        </div>
        <div className="absolute bottom-1/4 right-10 animate-pulse hidden lg:block" style={{animationDelay: '1s'}}>
          <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
            <Shield className="text-purple-400" size={24} />
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calculator" className="py-16 bg-gradient-to-b from-transparent to-black/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                <Calculator className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                {t.calculator.title}
              </h2>
            </div>
            <p className="text-xl text-white/80 mb-4">
              {t.calculator.subtitle}
            </p>
            {/* Speed Badge */}
            <div className="inline-block bg-green-500/20 border border-green-400/30 rounded-full px-6 py-2 mb-4">
              <span className="text-green-300 font-semibold flex items-center space-x-2">
                <Zap size={18} />
                <span>{t.calculator.speedBadge}</span>
              </span>
            </div>
            <p className="text-white/70 max-w-2xl mx-auto">
              {t.calculator.speedText}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            {/* Loan Amount Slider */}
            <div className="mb-8">
              <label className="block text-white font-semibold mb-3 flex items-center space-x-2">
                <Euro size={20} className="text-green-400" />
                <span>{t.calculator.loanAmount}</span>
              </label>
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6">
                <div className="text-center mb-4">
                  <span className="text-4xl md:text-5xl font-bold text-white">
                    {formatCurrency(loanAmount)}
                  </span>
                  {loanAmount >= 5000000 && (
                    <span className="ml-2 text-yellow-300 text-lg">+</span>
                  )}
                </div>
                <input
                  type="range"
                  min="100000"
                  max="10000000"
                  step="50000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-full h-3 bg-white/30 rounded-full appearance-none cursor-pointer slider-thumb"
                  style={{
                    background: `linear-gradient(to right, #22c55e 0%, #22c55e ${((loanAmount - 100000) / (10000000 - 100000)) * 100}%, rgba(255,255,255,0.3) ${((loanAmount - 100000) / (10000000 - 100000)) * 100}%, rgba(255,255,255,0.3) 100%)`
                  }}
                />
                <div className="flex justify-between text-white/70 text-sm mt-2">
                  <span>€100.000</span>
                  <span>€5.000.000</span>
                  <span>€10.000.000+</span>
                </div>
              </div>
              {loanAmount > 5000000 && (
                <div className="mt-3 flex items-center space-x-2 text-yellow-300 bg-yellow-500/20 rounded-xl px-4 py-2">
                  <AlertCircle size={18} />
                  <span className="text-sm">{t.calculator.aboveFiveInfo}</span>
                </div>
              )}
            </div>

            {/* Collateral Type */}
            <div className="mb-6">
              <label className="block text-white font-semibold mb-3 flex items-center space-x-2">
                <Shield size={20} className="text-purple-400" />
                <span>{t.calculator.collateralType}</span>
                <span className="text-red-400">*</span>
              </label>
              <select
                value={collateralType}
                onChange={(e) => setCollateralType(e.target.value)}
                className={`w-full bg-white/10 border ${formErrors.collateralType ? 'border-red-500' : 'border-white/30'} rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer`}
              >
                <option value="" className="bg-slate-800">-- {language === 'nl' ? 'Selecteer onderpand' : 'Select collateral'} --</option>
                {collateralOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-slate-800">
                    {option.label}
                  </option>
                ))}
              </select>
              {formErrors.collateralType && (
                <span className="text-red-400 text-sm mt-1">{t.calculator.required}</span>
              )}
            </div>

            {/* Other Collateral Input */}
            {collateralType === 'other' && (
              <div className="mb-6">
                <label className="block text-white font-semibold mb-2">
                  {t.calculator.otherCollateral} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={otherCollateral}
                  onChange={(e) => setOtherCollateral(e.target.value)}
                  className={`w-full bg-white/10 border ${formErrors.otherCollateral ? 'border-red-500' : 'border-white/30'} rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder={language === 'nl' ? 'Beschrijf uw onderpand...' : 'Describe your collateral...'}
                />
                {formErrors.otherCollateral && (
                  <span className="text-red-400 text-sm mt-1">{t.calculator.required}</span>
                )}
              </div>
            )}

            {/* Company Name */}
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2 flex items-center space-x-2">
                <Building2 size={20} className="text-blue-400" />
                <span>{t.calculator.companyName}</span>
                <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={`w-full bg-white/10 border ${formErrors.companyName ? 'border-red-500' : 'border-white/30'} rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder={language === 'nl' ? 'Naam van uw bedrijf' : 'Name of your company'}
              />
              {formErrors.companyName && (
                <span className="text-red-400 text-sm mt-1">{t.calculator.required}</span>
              )}
            </div>

            {/* Phone */}
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2 flex items-center space-x-2">
                <Phone size={20} className="text-green-400" />
                <span>{t.calculator.phone}</span>
                <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`w-full bg-white/10 border ${formErrors.phone ? 'border-red-500' : 'border-white/30'} rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder={language === 'nl' ? '+31 6 12345678' : '+31 6 12345678'}
              />
              {formErrors.phone && (
                <span className="text-red-400 text-sm mt-1">{t.calculator.required}</span>
              )}
            </div>

            {/* Email */}
            <div className="mb-8">
              <label className="block text-white font-semibold mb-2 flex items-center space-x-2">
                <Mail size={20} className="text-purple-400" />
                <span>{t.calculator.email}</span>
                <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full bg-white/10 border ${formErrors.email ? 'border-red-500' : 'border-white/30'} rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder={language === 'nl' ? 'uw@email.nl' : 'your@email.com'}
              />
              {formErrors.email && (
                <span className="text-red-400 text-sm mt-1">{t.calculator.required}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={formStatus === 'sending'}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-2"
            >
              <Send size={20} />
              <span>{formStatus === 'sending' ? t.calculator.sending : t.calculator.submit}</span>
            </button>

            {/* Tariffs Note */}
            <div className="mt-4 text-center text-white/60 text-sm">
              {t.calculator.tariffsNote}
            </div>

            {/* Status Messages */}
            {formStatus === 'success' && (
              <div className="mt-4 space-y-3">
                <div className="bg-green-500/20 border border-green-400/30 rounded-xl px-4 py-3 text-green-300 text-center flex items-center justify-center space-x-2">
                  <CheckCircle size={20} />
                  <span>{t.calculator.success}</span>
                </div>
                <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl px-4 py-3 text-blue-300 text-center text-sm">
                  <Mail size={16} className="inline mr-2" />
                  {t.calculator.successExtra}
                </div>
              </div>
            )}
            {formStatus === 'error' && (
              <div className="mt-4 bg-red-500/20 border border-red-400/30 rounded-xl px-4 py-3 text-red-300 text-center flex items-center justify-center space-x-2">
                <AlertCircle size={20} />
                <span>{t.calculator.error}</span>
              </div>
            )}
          </form>

        </div>
      </section>

      {/* Office Image Section */}
      <section className="py-8 bg-black/20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="/kantoor.jpg"
              alt="Bladt Bridge Loan Kantoor"
              className="w-full h-64 md:h-96 object-cover"
              onError={(e) => e.target.style.display = 'none'}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section id="intro" className="py-16 bg-black/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t.intro.title}
            </h2>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              {t.intro.text}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="text-blue-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t.value.fast.title}</h3>
                <p className="text-white/80">{t.value.fast.desc}</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="text-purple-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t.value.discrete.title}</h3>
                <p className="text-white/80">{t.value.discrete.desc}</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t.value.flexible.title}</h3>
                <p className="text-white/80">{t.value.flexible.desc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section id="cases" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t.cases.title}
            </h2>
            <p className="text-xl text-white/80">
              {t.cases.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((caseItem, index) => {
              const isExpanded = expandedCases[index];
              const truncatedText = caseItem.text.substring(0, 120) + '...';
              
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 hover:transform md:hover:scale-105">
                  <div className="mb-4 text-center md:text-left">
                    <span className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {caseItem.keyword}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 text-center md:text-left">{caseItem.title}</h3>
                  
                  {/* Desktop: full text */}
                  <p className="hidden md:block text-white/80 text-sm leading-relaxed">{caseItem.text}</p>
                  
                  {/* Mobile: truncated with expand */}
                  <div className="md:hidden text-center">
                    <p className="text-white/80 text-sm leading-relaxed">
                      {isExpanded ? caseItem.text : truncatedText}
                    </p>
                    <button
                      onClick={() => setExpandedCases(prev => ({ ...prev, [index]: !prev[index] }))}
                      className="mt-3 text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center justify-center space-x-1 mx-auto"
                    >
                      <span>{isExpanded ? t.cases.readLess : t.cases.readMore}</span>
                      <svg 
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LinkedIn Feed Section */}
      <section id="linkedin" className="py-16 bg-black/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <Linkedin className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                {t.linkedin.title}
              </h2>
            </div>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {t.linkedin.subtitle}
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-white/10">
            <div className="elfsight-app-b3db50b6-2773-41a5-a974-0982acad3987" data-elfsight-app-lazy></div>
          </div>
        </div>
      </section>

      {/* Sponsor Section */}
      <section id="sponsor" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-400/30">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start space-x-3 mb-4">
                  <Award className="text-orange-400" size={32} />
                  <h2 className="text-3xl md:text-4xl font-bold text-white">
                    {t.sponsor.title}
                  </h2>
                </div>
                <p className="text-xl text-orange-300 mb-6 font-semibold">
                  {t.sponsor.subtitle}
                </p>
                <p className="text-white/90 mb-6 leading-relaxed">
                  {t.sponsor.text}
                </p>
                {/* Button for both desktop and mobile */}
                <a
                  href={t.sponsor.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105"
                >
                  <span>{t.sponsor.readMore}</span>
                  <ExternalLink size={18} />
                </a>
              </div>
              <div className="relative">
                <img
                  src="/sponsor_contract.jpeg"
                  alt="Utrecht Dragons Sponsorcontract"
                  className="rounded-2xl shadow-2xl w-full"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rules Section */}
      <section id="rules" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t.rules.title}
            </h2>
            <p className="text-xl text-white/80">
              {t.rules.subtitle}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="grid md:grid-cols-2 gap-4">
                {t.rules.items.map((rule, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-white/90 text-sm">{rule}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-16 bg-black/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t.process.title}
            </h2>
            <p className="text-xl text-white/80">
              {t.process.subtitle}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="space-y-6">
              {t.process.steps.map((step, index) => (
                <div key={index} className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                  </div>
                  <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-white/80">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600/30 to-purple-600/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.cta.title}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            {t.cta.text}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+31613265193"
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl inline-flex items-center justify-center space-x-2"
            >
              <Phone size={20} />
              <span>{t.cta.callNow}</span>
            </a>
            <a
              href="mailto:c.bladt@bvgd.nl"
              className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center space-x-2"
            >
              <Mail size={20} />
              <span>{t.cta.emailNow}</span>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-black/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t.about.title}
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 mb-8">
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                {t.about.p1}
              </p>
              <p className="text-lg text-white/90 leading-relaxed">
                {t.about.p2}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6">{t.about.contact}</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <MapPin className="text-blue-400" size={20} />
                  <div>
                    <p className="text-white font-semibold">{t.about.company}</p>
                    <p className="text-white/80">{t.about.address1}</p>
                    <p className="text-white/80">{t.about.address2}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="text-green-400" size={20} />
                  <a href="tel:+31613265193" className="text-white/80 hover:text-white transition-colors">
                    06-13265193
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="text-purple-400" size={20} />
                  <a href="mailto:c.bladt@bvgd.nl" className="text-white/80 hover:text-white transition-colors">
                    c.bladt@bvgd.nl
                  </a>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/20">
                <p className="text-white/60 text-sm">
                  {t.about.disclaimer}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black/50 backdrop-blur-sm border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-white/60">
              {t.footer.copyright}
            </p>
          </div>
        </div>
      </footer>

      {/* Fixed CTA Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <a
          href="tel:+31613265193"
          className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
        >
          <Phone size={20} />
          <span>{t.cta.callNow}</span>
        </a>
      </div>
    </div>
  );
};

export default App;
